import { parsePhotoErase, PHOTO_ERASE_LIMITS, type PhotoEraseStroke } from './photo-erase.js';
import { isShapeKind, SHAPES, type ShapeKind } from './shapes.js';
/**
 * The portable half of the Designer's command set.
 *
 * Every edit a shopper or an author can make travels as a small JSON object:
 * the widget dispatches them, Studio dispatches them, and the HTTP/MCP
 * authoring endpoints accept the very same shapes. Three rules make that safe:
 *
 *  1. **Parse strictly, apply purely.** `parseDesignerParityCommand` rejects an
 *     unknown field rather than ignoring it — a typo must not silently do
 *     nothing — and `applyDesignerParityCommand` never mutates its input.
 *  2. **Every command is atomic.** It returns a whole new answer or it throws;
 *     there is no half-applied edit for an undo stack to get lost in.
 *  3. **The result is re-validated.** The product's own rules run again at the
 *     end, so no sequence of legal-looking commands can build an illegal design.
 *
 * `authoring: true` is the one privilege flag. Studio sets it; the storefront
 * never does. It is what separates "the author is composing the template" from
 * "the shopper is personalising it", and it is the only way past a `permanent`
 * or non-`interactive` layer.
 */

import { coverPhotoFrame } from './photo-framing.js';
import type { ImageCrop } from './types.js';
import {
  designAreaProblems,
  FIXED_TEXT_BOX_MIN_EM,
  LAYER_TEXT_MAX,
  layersExtent,
  newDesignLayerId,
  normalizeDesignAnswer,
  normalizeHex,
  resolveDesignAreaConfig,
  TEMPLATE_KEY_PATTERN,
  TEMPLATE_LABEL_MAX,
  TEMPLATE_ORIGINAL_MAX,
  type DesignAreaAnswer,
  type DesignAreaConfig,
  type DesignLayer,
  type DesignTemplateField,
  type DesignTextLayer,
} from './design-area.js';
import { STICKER_DEFAULTS } from './sticker.js';
import { DESIGNER_LOOKS, designerLook } from './designer-looks.js';
import { DESIGNER_PATTERN_MODES, parseDesignerPattern, validateDesignerQr, designerQrMinMm, type DesignerPattern } from './designer-pattern.js';
import type { Color } from './types.js';

/** A design saved to disk and loaded back, possibly into a different product. */
export interface DesignerDocument {
  format: 'personalizy-design';
  version: 1;
  sizeMm: { w: number; h: number };
  answer: DesignAreaAnswer;
}

export interface DesignerLayerProperties {
  name?: string;
  /** 0–1. The single transparency field; the UI shows it as 1–100. */
  opacity?: number;
  flipX?: boolean;
  flipY?: boolean;
  visible?: boolean;
  locked?: boolean;
  /** Authoring only: the Zazzle "Selling Option". `null` removes it. */
  template?: DesignTemplateField | null;
  /** Authoring only: the shopper may edit it but never delete it. */
  permanent?: boolean;
  /** Authoring only: `false` takes the layer out of the shopper's reach. */
  interactive?: boolean;
}

export interface DesignerTextPatch {
  text?: string;
  tracking?: number;
  lineSpacing?: number;
  outline?: { color: Color; width: number } | null;
  shadow?: { color: Color; offset: number; angle: number; blur: number } | null;
  /** Hug the text (`auto`, the default) or wrap inside the box (`fixed`). */
  boxMode?: 'auto' | 'fixed';
  /** Zazzle's anchor: where the lines sit in the box. Default `middle`. */
  anchorV?: 'top' | 'middle' | 'bottom';
}

/** Carried by EVERY command; Studio sets it, the storefront leaves it off. */
export interface DesignerAuthoring {
  authoring?: boolean;
}

export type DesignerParityCommand = DesignerAuthoring & (
  | { op: 'text'; ids: string[]; patch: DesignerTextPatch }
  /**
   * Zazzle's text box: `fixed` takes a width of its own and wraps inside it,
   * `auto` gives the box back to the measurement. `widthMm` belongs to `fixed`
   * only (absent = keep the width the layer already has); `anchorV` may ride
   * along with either.
   */
  | { op: 'textBox'; ids: string[]; boxMode: 'auto' | 'fixed'; widthMm?: number; anchorV?: 'top' | 'middle' | 'bottom' }
  | { op: 'background'; color: Color | null }
  | { op: 'pattern'; ids: string[]; pattern: DesignerPattern | null }
  | { op: 'orientation'; ids: string[]; orientation: 'horizontal' | 'vertical' }
  | { op: 'imageCrop'; ids: string[]; crop: ImageCrop | null; sizeMm?: { w: number; h: number } }
  | { op: 'imageErase'; ids: string[]; strokes: PhotoEraseStroke[] | null }
  | { op: 'imageMask'; ids: string[]; shape: ShapeKind | null }
  | { op: 'imageFit'; ids: string[]; fit: 'cover' | 'contain' }
  /**
   * Zazzle's image Effects: a photo look (`DESIGNER_LOOKS` id, or `grayscale` /
   * `sepia`; `null` = no filter) and white removal (`all`, `border` = only the
   * white connected to the picture edge, `null` = off). Omit either to keep it.
   */
  | { op: 'imageEffects'; ids: string[]; look?: string | null; whiteRemoval?: 'all' | 'border' | null }
  | { op: 'fitToZone'; ids: string[]; mode: 'fit' | 'fill' }
  | { op: 'reorder'; ids: string[]; index: number }
  | { op: 'flip'; ids: string[]; axis: 'x' | 'y' }
  | { op: 'properties'; ids: string[]; patch: DesignerLayerProperties }
  | { op: 'order'; ids: string[]; direction: 'front' | 'back' | 'forward' | 'backward' }
  | { op: 'qr'; url: string; id?: string }
  | { op: 'composition'; document: DesignerDocument; mode: 'replace' | 'append'; scale: 'fit' | 'physical' }
);

/** Which fields each op carries; `authoring` is implicit and allowed on all. */
const keys: Record<string, string[]> = {
  reorder: ['ids', 'index'],
  text: ['ids', 'patch'],
  textBox: ['ids', 'boxMode', 'widthMm', 'anchorV'],
  flip: ['ids', 'axis'],
  background: ['color'],
  pattern: ['ids', 'pattern'],
  orientation: ['ids', 'orientation'],
  imageCrop: ['ids', 'crop', 'sizeMm'],
  imageMask: ['ids', 'shape'],
  imageErase: ['ids', 'strokes'],
  imageFit: ['ids', 'fit'],
  imageEffects: ['ids', 'look', 'whiteRemoval'],
  fitToZone: ['ids', 'mode'],
  properties: ['ids', 'patch'],
  order: ['ids', 'direction'],
  qr: ['url', 'id'],
  composition: ['document', 'mode', 'scale'],
};

/** The privilege flag every command may carry, whatever its op. */
export const AUTHORING_FIELD = 'authoring';

/** Layer properties only an author may set. */
const AUTHORING_PROPERTIES = ['template', 'permanent', 'interactive'] as const;
const SHOPPER_PROPERTIES = ['name', 'opacity', 'flipX', 'flipY', 'visible', 'locked'] as const;

const object = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const validId = (value: unknown): boolean => typeof value === 'string' && value.length > 0 && value.length <= 40;
const finite = (value: unknown, min: number, max: number): boolean =>
  typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const only = (value: Record<string, unknown>, allowed: readonly string[]): boolean =>
  Object.keys(value).every((key) => allowed.includes(key));
const colour = (value: unknown): boolean =>
  object(value) && only(value, ['hex', 'alpha']) && Boolean(normalizeHex(value.hex))
  && (value.alpha === undefined || finite(value.alpha, 0, 1));

// ── Documents ────────────────────────────────────────────────────────────────

export function parseDesignerDocument(raw: unknown): DesignerDocument {
  if (!object(raw) || !only(raw, ['format', 'version', 'sizeMm', 'answer']) || raw.format !== 'personalizy-design' || raw.version !== 1) {
    throw new Error('Unsupported design file');
  }
  if (!object(raw.sizeMm) || !only(raw.sizeMm, ['w', 'h']) || !finite(raw.sizeMm.w, 1, 2000) || !finite(raw.sizeMm.h, 1, 2000)) {
    throw new Error('Invalid design dimensions');
  }
  // A file is untrusted input from a shopper's disk; cap it before parsing it.
  if (new TextEncoder().encode(JSON.stringify(raw)).length > 128000) throw new Error('Design file exceeds 128 KB');
  const answer = normalizeDesignAnswer(raw.answer);
  // Normalisation DROPS a layer it cannot read. For a whole document that is
  // not acceptable — losing one layer silently is worse than refusing the file.
  const sameCount = Boolean(answer) && object(raw.answer) && Array.isArray(raw.answer.layers)
    && answer!.layers.length === raw.answer.layers.length;
  if (!answer || !sameCount || answer.layers.length > 60) throw new Error('Invalid design layers');
  const sizeMm = raw.sizeMm as { w: number; h: number };
  // Judged against the file's OWN dimensions and the loosest possible rules:
  // whether it suits the product it is being loaded into is decided on import.
  const problems = designAreaProblems(
    { sizeMm, maxLayers: 60, image: { maxCount: 60 }, sticker: { allowed: true }, text: { maxChars: 2000, maxLines: 100, minSizeMm: 0.5, maxSizeMm: 2000 } },
    answer,
  );
  if (problems.length) throw new Error(problems[0]!.detail);
  return { format: 'personalizy-design', version: 1, sizeMm: { ...sizeMm }, answer };
}

// ── Parsing ──────────────────────────────────────────────────────────────────

/** The two box fields, shared by the `text` patch and the `textBox` op. */
const BOX_MODES = ['auto', 'fixed'] as const;
const ANCHORS = ['top', 'middle', 'bottom'] as const;

function parseTextPatch(patch: unknown): void {
  if (!object(patch) || !Object.keys(patch).length || !only(patch, ['text', 'tracking', 'lineSpacing', 'outline', 'shadow', 'boxMode', 'anchorV'])) {
    throw new Error('Invalid text properties');
  }
  if (patch.text !== undefined && (typeof patch.text !== 'string' || patch.text.length > LAYER_TEXT_MAX)) throw new Error('Invalid text properties');
  if (patch.tracking !== undefined && !finite(patch.tracking, -500, 1000)) throw new Error('Invalid text properties');
  if (patch.lineSpacing !== undefined && !finite(patch.lineSpacing, 0.5, 3)) throw new Error('Invalid text properties');
  if (patch.boxMode !== undefined && !BOX_MODES.includes(patch.boxMode as never)) throw new Error('Invalid text box mode');
  if (patch.anchorV !== undefined && !ANCHORS.includes(patch.anchorV as never)) throw new Error('Invalid text anchor');
  if (patch.outline !== undefined && patch.outline !== null) {
    const outline = patch.outline;
    if (!object(outline) || !only(outline, ['color', 'width']) || !colour(outline.color) || !finite(outline.width, 0, 0.5)) throw new Error('Invalid outline');
  }
  if (patch.shadow !== undefined && patch.shadow !== null) {
    const shadow = patch.shadow;
    const shape = object(shadow) && only(shadow, ['color', 'offset', 'angle', 'blur']) && colour(shadow.color);
    if (!shape || !finite(shadow.offset, 0, 100) || !finite(shadow.blur, 0, 100) || !finite(shadow.angle, -360, 360)) throw new Error('Invalid shadow');
  }
}

/** A template field as it arrives in a `properties` patch. */
function parseTemplateField(value: unknown): void {
  if (!object(value) || !only(value, ['label', 'key', 'editOnCard', 'mustReplace', 'original'])) throw new Error('Invalid template field');
  if (typeof value.label !== 'string' || !value.label.trim() || value.label.length > TEMPLATE_LABEL_MAX) throw new Error('A template object needs a product page label');
  if (value.key !== undefined && (typeof value.key !== 'string' || !TEMPLATE_KEY_PATTERN.test(value.key))) throw new Error('Invalid template key');
  if (value.original !== undefined && (typeof value.original !== 'string' || value.original.length > TEMPLATE_ORIGINAL_MAX)) throw new Error('Invalid template original');
  if (['editOnCard', 'mustReplace'].some((flag) => value[flag] !== undefined && typeof value[flag] !== 'boolean')) throw new Error('Invalid template field');
}

function parsePropertiesPatch(patch: unknown, authoring: boolean): void {
  const allowed = authoring ? [...SHOPPER_PROPERTIES, ...AUTHORING_PROPERTIES] : SHOPPER_PROPERTIES;
  if (!object(patch) || !Object.keys(patch).length || !only(patch, allowed)) throw new Error('Invalid layer properties');
  if (patch.name !== undefined && (typeof patch.name !== 'string' || patch.name.length > TEMPLATE_LABEL_MAX)) throw new Error('Invalid layer properties');
  if (patch.opacity !== undefined && !finite(patch.opacity, 0, 1)) throw new Error('Invalid layer properties');
  const flags = ['flipX', 'flipY', 'visible', 'locked', 'permanent', 'interactive'];
  if (flags.some((flag) => patch[flag] !== undefined && typeof patch[flag] !== 'boolean')) throw new Error('Invalid layer properties');
  if (patch.template !== undefined && patch.template !== null) parseTemplateField(patch.template);
}

/**
 * Strict schema check. Returns `undefined` — not an error — when the op is not
 * one of ours, so `parseDesignerCommand` can try its own set next.
 */
export function parseDesignerParityCommand(raw: unknown): DesignerParityCommand | undefined {
  if (!object(raw) || typeof raw.op !== 'string' || !keys[raw.op]) return undefined;
  const op = raw.op;
  if (!only(raw, ['op', AUTHORING_FIELD, ...keys[op]!])) throw new Error('Unknown Designer command field');
  if (raw[AUTHORING_FIELD] !== undefined && typeof raw[AUTHORING_FIELD] !== 'boolean') throw new Error('Invalid authoring flag');
  const authoring = raw[AUTHORING_FIELD] === true;
  if (keys[op]!.includes('ids')) {
    const ids = raw.ids;
    const usable = Array.isArray(ids) && ids.length >= 1 && ids.length <= 60 && ids.every(validId) && new Set(ids).size === ids.length;
    if (!usable) throw new Error('Select 1–60 unique layers');
  }
  if (op === 'background' && raw.color !== null && !colour(raw.color)) throw new Error('Invalid background colour');
  if (op === 'pattern' && raw.pattern !== null) parseDesignerPattern(raw.pattern);
  if (op === 'orientation' && !['horizontal', 'vertical'].includes(String(raw.orientation))) throw new Error('Invalid text orientation');
  if (op === 'imageCrop' && raw.crop !== null) {
    const crop = raw.crop;
    if (!object(crop) || !only(crop, ['cx', 'cy', 'zoom', 'rotation'])
      || !finite(crop.cx, 0, 1) || !finite(crop.cy, 0, 1) || !finite(crop.zoom, 1, 8)
      || (crop.rotation !== undefined && !finite(crop.rotation, -45, 45))) throw new Error('Invalid photo crop');
  }
  if (op === 'imageCrop' && raw.sizeMm !== undefined && (!object(raw.sizeMm) || !only(raw.sizeMm, ['w', 'h']) || !finite(raw.sizeMm.w, 0.5, 5000) || !finite(raw.sizeMm.h, 0.5, 5000))) throw new Error('Invalid crop frame size');
  if (op === 'imageErase' && raw.strokes !== null) parsePhotoErase(raw.strokes);
  if (op === 'imageMask' && raw.shape !== null && !isShapeKind(String(raw.shape))) throw new Error('Invalid photo mask');
  if (op === 'imageFit' && !['cover', 'contain'].includes(String(raw.fit))) throw new Error('Invalid image fit');
  if (op === 'imageEffects') {
    if (raw.look === undefined && raw.whiteRemoval === undefined) throw new Error('Name a look or a white removal');
    const look = raw.look;
    if (look !== undefined && look !== null && look !== 'grayscale' && look !== 'sepia' && !designerLook(look)) throw new Error('Unknown photo look');
    if (raw.whiteRemoval !== undefined && raw.whiteRemoval !== null && !['all', 'border'].includes(String(raw.whiteRemoval))) throw new Error('Invalid white removal');
  }
  if (op === 'fitToZone' && !['fit', 'fill'].includes(String(raw.mode))) throw new Error('Invalid zone fit');
  if (op === 'reorder' && (!finite(raw.index, 0, 59) || !Number.isInteger(raw.index))) throw new Error('Invalid layer index');
  if (op === 'flip' && !['x', 'y'].includes(String(raw.axis))) throw new Error('Invalid flip axis');
  if (op === 'order' && !['front', 'back', 'forward', 'backward'].includes(String(raw.direction))) throw new Error('Invalid layer order');
  if (op === 'text') parseTextPatch(raw.patch);
  if (op === 'textBox') {
    if (!BOX_MODES.includes(raw.boxMode as never)) throw new Error('Invalid text box mode');
    if (raw.anchorV !== undefined && !ANCHORS.includes(raw.anchorV as never)) throw new Error('Invalid text anchor');
    // A width is a property of a fixed box and of nothing else; an auto box is
    // measured, so a width there would be a number that never took effect.
    if (raw.widthMm !== undefined && raw.boxMode !== 'fixed') throw new Error('Only a fixed text box has a width of its own');
    if (raw.widthMm !== undefined && !finite(raw.widthMm, 1, 2000)) throw new Error('Invalid text box width');
  }
  if (op === 'properties') parsePropertiesPatch(raw.patch, authoring);
  if (op === 'qr') {
    validateDesignerQr(raw.url);
    if (raw.id !== undefined && !validId(raw.id)) throw new Error('Invalid QR layer ID');
  }
  if (op === 'composition') {
    parseDesignerDocument(raw.document);
    if (!['replace', 'append'].includes(String(raw.mode)) || !['fit', 'physical'].includes(String(raw.scale))) throw new Error('Invalid composition transfer');
  }
  return structuredClone(raw) as unknown as DesignerParityCommand;
}

// ── The template model's two refusals ────────────────────────────────────────

export const PERMANENT_REFUSAL = 'This element is part of the design and cannot be removed';
export const NON_INTERACTIVE_REFUSAL = 'This element is part of the design and cannot be changed';

/**
 * What an author may do and a shopper may not.
 *
 * A non-interactive layer is out of the shopper's reach entirely — it prints as
 * the author drew it. A permanent layer they may move, scale and retype; only
 * removing it is refused, which is `assertRemovable`'s job.
 */
export function assertEditable(layers: DesignLayer[], authoring: boolean | undefined): void {
  if (authoring) return;
  if (layers.some((layer) => layer.interactive === false)) throw new Error(NON_INTERACTIVE_REFUSAL);
}

/** The same rule for the operations that make a layer disappear. */
export function assertRemovable(layers: DesignLayer[], authoring: boolean | undefined): void {
  if (authoring) return;
  if (layers.some((layer) => layer.permanent)) throw new Error(PERMANENT_REFUSAL);
}

// ── Applying ─────────────────────────────────────────────────────────────────

/** Scales a layer's size (not its position) by `factor`: box, type size, stroke, pattern gaps. */
export function resizeDesignLayer(layer: DesignLayer, factor: number): void {
  resize(layer, factor);
}

/** Scales a layer and everything about it measured in millimetres. */
function resize(layer: DesignLayer, factor: number): void {
  layer.box.w *= factor;
  layer.box.h *= factor;
  if (layer.kind === 'text') {
    layer.sizeMm *= factor;
    if (layer.textWidthMm) layer.textWidthMm *= factor;
  }
  if (layer.kind === 'shape' && layer.stroke) layer.stroke.widthMm *= factor;
  if (layer.pattern) { layer.pattern.gapXmm *= factor; layer.pattern.gapYmm *= factor; }
}

/** The sticker settings a replaced composition must keep, if any. */
function carriedSticker(config: DesignAreaConfig, answer: DesignAreaAnswer) {
  if (answer.sticker) return answer.sticker;
  if (config.sticker?.required) return config.sticker.defaults ?? STICKER_DEFAULTS;
  return undefined;
}

function applyComposition(config: DesignAreaConfig, answer: DesignAreaAnswer, next: DesignAreaAnswer, command: DesignerParityCommand & { op: 'composition' }): DesignAreaAnswer {
  const cfg = resolveDesignAreaConfig(config);
  const document = parseDesignerDocument(command.document);
  const copied = structuredClone(document.answer);
  // `physical` keeps every millimetre as authored — the shopper is moving a
  // design between two products of the same size. `fit` rescales it into the
  // safe area of a zone that may be a different shape entirely.
  const scale = command.scale === 'physical'
    ? 1
    : Math.min((cfg.sizeMm.w - 2 * cfg.safeMarginMm) / document.sizeMm.w, (cfg.sizeMm.h - 2 * cfg.safeMarginMm) / document.sizeMm.h);
  if (scale <= 0) throw new Error('Composition does not fit the safe area');
  // Fresh ids: the incoming layers must not collide with what is already here,
  // and a group must stay a group without borrowing another design's group id.
  const groups = new Map<string, string>();
  for (const layer of copied.layers) {
    layer.id = newDesignLayerId();
    if (layer.groupId) {
      if (!groups.has(layer.groupId)) groups.set(layer.groupId, newDesignLayerId());
      layer.groupId = groups.get(layer.groupId);
    }
    layer.box.x = cfg.sizeMm.w / 2 + (layer.box.x - document.sizeMm.w / 2) * scale;
    layer.box.y = cfg.sizeMm.h / 2 + (layer.box.y - document.sizeMm.h / 2) * scale;
    resize(layer, scale);
  }
  if (command.mode !== 'replace') {
    next.layers.push(...copied.layers);
    return next;
  }
  // Replacing throws the current design away, so it is a removal as far as the
  // author's permanent layers are concerned.
  assertRemovable(answer.layers, command.authoring);
  const sticker = carriedSticker(config, answer);
  return { ...copied, ...(sticker ? { sticker } : {}) };
}

function applyQr(config: DesignAreaConfig, next: DesignAreaAnswer, command: DesignerParityCommand & { op: 'qr' }): void {
  const cfg = resolveDesignAreaConfig(config);
  const url = validateDesignerQr(command.url);
  if (!command.id) {
    // A new code is born at the smallest side that will scan for its payload.
    const side = designerQrMinMm(url);
    next.layers.push({ id: newDesignLayerId(), kind: 'qr', url, box: { x: cfg.sizeMm.w / 2, y: cfg.sizeMm.h / 2, w: side, h: side } });
    return;
  }
  const layer = next.layers.find((candidate) => candidate.id === command.id);
  if (layer?.kind !== 'qr' || layer.locked) throw new Error('Select an unlocked QR layer');
  assertEditable([layer], command.authoring);
  layer.url = url;
}

function applyFlip(selected: DesignLayer[], axis: 'x' | 'y'): void {
  // The selection flips as one piece: mirror each centre about the selection's
  // own midline, negate the rotations, and toggle the per-layer mirror flag.
  const bounds = layersExtent(selected);
  const centre = axis === 'x' ? (bounds.left + bounds.right) / 2 : (bounds.top + bounds.bottom) / 2;
  for (const layer of selected) {
    layer.box[axis] = 2 * centre - layer.box[axis];
    layer.box.rotation = -(layer.box.rotation ?? 0);
    if (axis === 'x') layer.flipX = !layer.flipX; else layer.flipY = !layer.flipY;
  }
}

/**
 * The box mode and the anchor on one text layer, stored the way the model
 * stores them: by their non-default, so an untouched layer carries neither.
 *
 * Switching to a fixed box is the one that costs something. The width becomes
 * the shopper's own (the side handles, or `widthMm` here) and has to be able to
 * hold at least one em, or the "wrap" is a column of broken letters; and the
 * bend goes, because a line that wraps has no single arc to follow. The HEIGHT
 * is deliberately not touched: only a renderer knows how many lines the text
 * wrapped into, so the designer sets it from `fixedTextBoxHeightMm` once it has
 * measured.
 */
function setTextBox(
  layer: DesignTextLayer,
  boxMode: 'auto' | 'fixed' | undefined,
  anchorV: 'top' | 'middle' | 'bottom' | undefined,
  widthMm?: number,
): void {
  if (anchorV === 'middle') delete layer.anchorV;
  else if (anchorV) layer.anchorV = anchorV;
  if (boxMode === 'auto') delete layer.boxMode;
  if (boxMode !== 'fixed') return;
  // Vertical text is a column of stacked glyphs — a layout with nothing to wrap.
  if (layer.orientation === 'vertical') throw new Error('Vertical text sizes itself; make it horizontal before fixing its width');
  const width = widthMm ?? layer.box.w;
  const minimum = layer.sizeMm * FIXED_TEXT_BOX_MIN_EM;
  if (width < minimum - 1e-6) throw new Error(`A fixed text box must be at least one em wide (${Math.round(minimum * 10) / 10} mm)`);
  layer.boxMode = 'fixed';
  layer.box.w = width;
  delete layer.bend;
  // Measured for the old hugging box; a fixed one never consults it.
  delete layer.textWidthMm;
}

function applyTextPatch(selected: DesignLayer[], patch: DesignerTextPatch): void {
  for (const layer of selected) {
    if (layer.kind !== 'text') throw new Error('Select text layers');
    const { outline, shadow, boxMode, anchorV, ...rest } = patch;
    Object.assign(layer, rest);
    setTextBox(layer, boxMode, anchorV);
    if (outline === null) delete layer.outline; else if (outline) layer.outline = structuredClone(outline);
    if (shadow === null) delete layer.shadow; else if (shadow) layer.shadow = structuredClone(shadow);
    // The measured width belongs to the old string and the old tracking; drop
    // it so the designer re-measures before any curve is laid out.
    if (patch.text !== undefined || patch.tracking !== undefined) delete layer.textWidthMm;
    // Any hand edit means the look is no longer one of the preset chips.
    delete layer.styleId;
  }
}

function applyProperties(selected: DesignLayer[], patch: DesignerLayerProperties): void {
  const { template, ...rest } = patch;
  for (const layer of selected) {
    Object.assign(layer, rest);
    if (template === null) delete layer.template;
    else if (template) layer.template = structuredClone(template);
    // `interactive: true` is the default, so store it by absence.
    if (layer.interactive === true) delete layer.interactive;
    if (layer.permanent === false) delete layer.permanent;
  }
}

function applyFitToZone(cfg: ReturnType<typeof resolveDesignAreaConfig>, selected: DesignLayer[], mode: 'fit' | 'fill'): void {
  const bounds = layersExtent(selected);
  const width = bounds.right - bounds.left, height = bounds.bottom - bounds.top;
  // "Uklopi" respects the safe margin and shows everything; "Popuni" ignores
  // the margin and lets the overflow be trimmed — Zazzle's Fit and Fill.
  const margin = mode === 'fit' ? cfg.safeMarginMm : 0;
  const scale = (mode === 'fit' ? Math.min : Math.max)((cfg.sizeMm.w - 2 * margin) / width, (cfg.sizeMm.h - 2 * margin) / height);
  if (scale <= 0) throw new Error('Selection does not fit this zone');
  for (const layer of selected) {
    layer.box.x = cfg.sizeMm.w / 2 + (layer.box.x - (bounds.left + bounds.right) / 2) * scale;
    layer.box.y = cfg.sizeMm.h / 2 + (layer.box.y - (bounds.top + bounds.bottom) / 2) * scale;
    resize(layer, scale);
  }
}

function applyOrder(next: DesignAreaAnswer, ids: Set<string>, direction: 'front' | 'back' | 'forward' | 'backward'): void {
  const selected = next.layers.filter((layer) => ids.has(layer.id));
  if (direction === 'front' || direction === 'back') {
    const rest = next.layers.filter((layer) => !ids.has(layer.id));
    next.layers = direction === 'front' ? [...rest, ...selected] : [...selected, ...rest];
    return;
  }
  // One step: walk from the end the selection is moving towards, so a run of
  // selected layers slides past an unselected one together instead of
  // leapfrogging over each other and ending up reordered among themselves.
  const forward = direction === 'forward';
  const layers = next.layers;
  for (let i = forward ? layers.length - 2 : 1; forward ? i >= 0 : i < layers.length; i += forward ? -1 : 1) {
    const swap = i + (forward ? 1 : -1);
    if (ids.has(layers[i]!.id) && !ids.has(layers[swap]!.id)) {
      [layers[i], layers[swap]] = [layers[swap]!, layers[i]!];
    }
  }
}

/** Portable, atomic operations; authoring endpoints add ownership and revision checks. */
export function applyDesignerParityCommand(config: DesignAreaConfig, answer: DesignAreaAnswer, c: DesignerParityCommand): DesignAreaAnswer {
  const cfg = resolveDesignAreaConfig(config);
  let next = structuredClone(answer);

  if (c.op === 'background') {
    // A printed backdrop and a die-cut contour are different products; refuse
    // here as well as in validation, so the message names the actual conflict.
    if (c.color && next.sticker) throw new Error('Clear the printed background before enabling Sticker');
    if (c.color) next.background = c.color; else delete next.background;
  } else if (c.op === 'composition') {
    next = applyComposition(config, answer, next, c);
  } else if (c.op === 'qr') {
    applyQr(config, next, c);
  } else {
    const ids = new Set(c.ids);
    if (c.ids.some((id) => !next.layers.some((layer) => layer.id === id))) throw new Error('A selected layer no longer exists');
    // Property edits and text/image content target individual group members;
    // anything that moves or restacks takes the whole group with it.
    if (c.op === 'fitToZone' || c.op === 'order' || c.op === 'flip' || c.op === 'reorder') {
      const groups = new Set(next.layers.filter((layer) => ids.has(layer.id) && layer.groupId).map((layer) => layer.groupId));
      next.layers.filter((layer) => layer.groupId && groups.has(layer.groupId)).forEach((layer) => ids.add(layer.id));
    }
    const selected = next.layers.filter((layer) => ids.has(layer.id));
    assertEditable(selected, c.authoring);
    // Unlocking is the one edit a locked layer accepts — otherwise the lock
    // would be a trap with no key.
    const onlyUnlock = c.op === 'properties' && Object.keys(c.patch).length === 1 && c.patch.locked === false;
    if (!onlyUnlock && selected.some((layer) => layer.locked)) throw new Error('Unlock selected layers before editing them');

    if (c.op === 'flip') applyFlip(selected, c.axis);
    if (c.op === 'text') applyTextPatch(selected, c.patch);
    if (c.op === 'textBox') {
      for (const layer of selected) {
        if (layer.kind !== 'text') throw new Error('Select text layers');
        setTextBox(layer, c.boxMode, c.anchorV, c.widthMm);
      }
    }
    if (c.op === 'properties') applyProperties(selected, c.patch);
    if (c.op === 'pattern') {
      for (const layer of selected) {
        if (c.pattern) layer.pattern = structuredClone(c.pattern); else delete layer.pattern;
      }
    }
    if (c.op === 'orientation') {
      for (const layer of selected) {
        if (layer.kind !== 'text') throw new Error('Select text layers');
        layer.orientation = c.orientation;
        // Stacked glyphs, an arc and a wrapped paragraph are three different
        // layouts of the same line; going vertical gives up the other two.
        if (c.orientation === 'vertical') { delete layer.bend; delete layer.boxMode; }
        delete layer.textWidthMm;
      }
    }
    if (c.op === 'imageCrop') {
      for (const layer of selected) {
        if (layer.kind !== 'image') throw new Error('Select photo layers');
        layer.fit = 'cover';
        if (c.sizeMm) layer.box = { ...layer.box, ...c.sizeMm };
        if (c.crop === null) delete layer.crop;
        else layer.crop = layer.sourcePx ? coverPhotoFrame(layer.sourcePx, layer.box, c.crop).crop : { ...c.crop };
      }
    }
    if (c.op === 'imageErase') {
      for (const layer of selected) {
        if (layer.kind !== 'image') throw new Error('Select photo layers');
        if (c.strokes === null || !c.strokes.length) delete layer.erase;
        else layer.erase = parsePhotoErase(c.strokes);
      }
    }
    if (c.op === 'imageMask') {
      for (const layer of selected) {
        if (layer.kind !== 'image') throw new Error('Select photo layers');
        if (c.shape === null) delete layer.maskShape; else layer.maskShape = c.shape;
      }
    }
    if (c.op === 'imageEffects') {
      if (!cfg.image.allowFilters) throw new Error('Photo effects are not available for this product');
      for (const layer of selected) {
        if (layer.kind !== 'image') throw new Error('Select photo layers');
        let effects = [...(layer.effects ?? [])];
        // One look at a time: a look replaces the previous one and the two
        // colour presets it stands beside in the picker.
        if (c.look !== undefined) {
          effects = effects.filter((effect) => !['look', 'grayscale', 'sepia'].includes(effect.kind));
          if (c.look === 'grayscale' || c.look === 'sepia') effects.push({ kind: c.look });
          else if (c.look) effects.push({ kind: 'look', id: c.look });
        }
        if (c.whiteRemoval !== undefined) {
          effects = effects.filter((effect) => effect.kind !== 'whiteTransparent');
          if (c.whiteRemoval === 'all') effects.push({ kind: 'whiteTransparent' });
          if (c.whiteRemoval === 'border') effects.push({ kind: 'whiteTransparent', connected: true });
        }
        if (effects.length) layer.effects = effects; else delete layer.effects;
      }
    }
    if (c.op === 'imageFit') {
      for (const layer of selected) {
        if (layer.kind !== 'image') throw new Error('Select photo layers');
        layer.fit = c.fit;
        // Framing remains available when switching back from contain.
        // Selecting an already-active fit must never reset a customer's crop.
      }
    }
    if (c.op === 'fitToZone') applyFitToZone(cfg, selected, c.mode);
    if (c.op === 'reorder') {
      const rest = next.layers.filter((layer) => !ids.has(layer.id));
      rest.splice(Math.min(c.index, rest.length), 0, ...selected);
      next.layers = rest;
    }
    if (c.op === 'order') applyOrder(next, ids, c.direction);
  }

  // The product's own rules, last: no legal-looking command may leave behind an
  // answer the commit would refuse. Resolution and asset provenance are the
  // caller's business — they need a database this function does not have.
  const problems = designAreaProblems(config, next).filter((p) => !['low-resolution', 'asset-unknown', 'library-not-allowed'].includes(p.code));
  if (problems.length) throw new Error(problems[0]!.detail);
  const normalized = normalizeDesignAnswer(next);
  if (!normalized) throw new Error('Invalid design');
  return normalized;
}

export const DESIGNER_PARITY_CAPABILITIES = {
  operations: Object.keys(keys),
  commandFields: keys,
  authoring: 'Every command may carry authoring: true. Studio sets it; it is required to edit a non-interactive layer, to remove or replace a permanent one, and to set template/permanent/interactive properties.',
  selection: 'ids: 1–60 unique existing layer IDs. Group transforms and order expand the group; content/property changes can edit a member.',
  reorder: 'index 0–59, bottom to top; selected group remains together',
  flip: ['x', 'y'],
  orientation: ['horizontal', 'vertical'],
  imageCrop: { sizeMm: 'Optional physical crop frame width/height, 0.5–5000 mm; keeps centre and layer rotation, subject to product bounds', centre: 'cx/cy 0–1 in original source coordinates', zoom: [1, 8], rotation: [-45, 45], reset: null, framing: 'cover, clamped with automatic fill on straighten; shared editor/preview/print geometry' },
  imageErase: { ...PHOTO_ERASE_LIMITS, modes: ['erase', 'restore'], reset: null, coordinates: 'Original unmirrored source fractions 0–1. Diameter is a fraction of the shorter source edge. Ordered strokes replace the mask; restore reveals original source alpha. Preview/print/Sticker share the same native mask.' },
  imageMask: { shapes: Object.keys(SHAPES), reset: null, framing: 'Native silhouette in the photo frame; preserves original source, crop and reflection. Shared preview, print, Sticker alpha and vector fallback.' },
  imageFit: ['cover', 'contain'],
  imageEffects: { look: ['grayscale', 'sepia', ...DESIGNER_LOOKS.map((look) => look.id), null], whiteRemoval: ['all', 'border', null], requires: 'config.image.allowFilters' },
  fitToZone: ['fit', 'fill'],
  order: ['front', 'back', 'forward', 'backward'],
  composition: { mode: ['replace', 'append'], scale: ['fit', 'physical'], identity: 'New layer/group IDs; target product validation and ownership are mandatory' },
  editorView: { scope: 'Local editor preferences; never print data', zoom: [0.25, 4], toggles: ['panMode', 'guidelines', 'grid', 'rulers', 'snap', 'lockAspect', 'transparency', 'miniToolbar', 'darkMode', 'review'] },
  background: 'Printed colour, null clears. Separate from the editor viewing backdrop; incompatible with Sticker.',
  pattern: { modes: [...DESIGNER_PATTERN_MODES], centered: 'basic grid anchored on the zone centre, symmetric part-tiles at the edges; the motif position does not move it', gapMm: [0, 200], maxTilesPerLayer: 400, maxTilesPerZone: 800 },
  qr: { payload: 'HTTP(S) URL, no credentials', maxUtf8Bytes: 256, errorCorrection: 'M', quietZone: 'at least four modules', minimumSide: '20 + UTF-8 bytes / 16, rounded up, millimetres', appearance: 'opaque black on white; square and unmirrored' },
  text: { fields: ['text', 'tracking', 'lineSpacing', 'outline', 'shadow', 'boxMode', 'anchorV'], outlineWidth: '0–0.5 em; null clears', shadow: 'offset/blur 0–100 percent of font size; angle -360–360; null clears', tracking: [-500, 1000], lineSpacing: [0.5, 3] },
  textBox: {
    boxMode: ['auto', 'fixed'],
    anchorV: ['top', 'middle', 'bottom'],
    widthMm: 'fixed only, 1–2000 mm and at least one em; absent keeps the layer’s width',
    height: 'not set here — the wrapped line count is a renderer measurement; use fixedTextBoxHeightMm(lines, sizeMm, lineSpacing)',
    effects: 'fixed clears bend and the measured textWidthMm; vertical text refuses fixed, and turning text vertical returns it to auto',
  },
  properties: [...SHOPPER_PROPERTIES],
  authoringProperties: [...AUTHORING_PROPERTIES],
  template: { label: `1–${TEMPLATE_LABEL_MAX} characters`, key: 'lowercase a–z, 0–9, - or _, up to 40; links duplicate fields', editOnCard: 'quick field on the product card', mustReplace: 'blocks checkout until the shopper changes it', original: `the authored text or asset id, up to ${TEMPLATE_ORIGINAL_MAX} characters` },
  document: { format: 'personalizy-design', version: 1, maxLayers: 60, maxBytes: 128000, units: 'mm', resources: 'Existing tenant/session-owned asset IDs; transfer does not import or grant resource access' },
} as const;
